/* eslint-disable react-hooks/exhaustive-deps */
import { useViewResolverContext } from "@ldp/view-resolver-lite";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Masthead } from "@ldp-web/component-library/ui/organisms/masthead";
import {
  ProvinceListItem,
  useProvinceContext,
  useSDMCartContext,
  useSDMCustomerContext,
  useSDMRouterContext,
} from "@ldp/sdm-contexts";
import { useRedirectLink } from "../../utils/common-utils";
import { useSimpleTranslation } from "@ldp/simple-i18n";
import {
  useAnalyticsContext,
  useFeatureFlags,
  useSettingsContext,
} from "@ldp/common-contexts";
import { useConfig, useUpdateQueryParam } from "../../hooks";
import { Dropdown } from "@ldp-web/component-library/types";
import { SDMLanguageType, TSDMCta } from "@ldp/common-types";
import { ErrorBoundary } from "@ldp/error-boundary";
import { BreadcrumbsSkeleton } from "@ldp-web/component-library/ui/skeletons/breadcrumbs-skeleton";
import {
  IQueryItem,
  MastheadCustomerAvatarProps,
} from "./masthead.component.interface";
import {
  buildTypeaheadSuggestions,
  fetchColdStartSearch,
  fetchTypeAheadSearch,
  trimSectionKey,
} from "../../utils";
import {
  getCiamAccessToken,
  getSnowplowUserIdentifications,
} from "@ldp/media-utils";
import { CustomAutoCompleteSlot } from "@ldp-web/component-library/ui/molecules/masthead-main-bar";
import {
  AutoCompleteHighlightItem,
  Autocomplete,
  AutocompleteSearchContentSlot,
  type IAutoCompleteRef,
  Item,
  Section,
  Skeleton,
} from "@iceberg/ui";
import { useLocalStorage } from "usehooks-ts";
import {
  SEARCH_CHAR_COUNT,
  SEARCH_COPY_MAP,
  SearchSections,
  TRENDING_SEARCHES,
  TRENDING_SEARCH_ITEMS_DISPLAY_LIMIT,
  YOUR_SEARCH_ITEMS,
  YOUR_SEARCH_ITEMS_DISPLAY_LIMIT,
} from "../../constants/masthead-constants";
import {
  SEARCH_TERMS_DATA_KEY,
  SearchTermsType,
} from "../../constants/analytics-constants";
import { Clock8 } from "lucide-react";
import { SearchColdStartComponent } from "./search-coldstart.component";
import { TViewDefinition } from "@ldp/view-resolver-utils";
import { Box } from "@ldp-web/component-library/ui/atoms/box";
import { useTheme } from "@chakra-ui/react";

export interface ITypeAheadSearchItem {
  /* The search term */
  suggestion: string;
  /* Loading state */
  isLoading?: boolean;
  /* Empty message state */
  isEmptyMessage?: boolean;
}

interface MainContentCollection {
  components: any[];
}

export const getInitials = (
  customerFirstName: string | null,
  customerLastName: string | null
): string => {
  if (customerFirstName === null && customerLastName === null) {
    return "";
  }
  const firstNameInitial = customerFirstName
    ? customerFirstName.charAt(0).toUpperCase()
    : "";
  const lastNameInitial = customerLastName
    ? customerLastName.charAt(0).toUpperCase()
    : "";
  return `${firstNameInitial} ${lastNameInitial}`;
};

//TODO add logic to pass these initials to masthead component PHARM-26662
export const useSdmCustomerInitials = ({
  firstName,
  lastName,
}: MastheadCustomerAvatarProps) =>
  useMemo(() => {
    return getInitials(firstName, lastName);
  }, [firstName, lastName]);

/** renders masthead layout */
export const MastheadComponent = () => {
  const {
    viewDefinition: { header },
  } = useViewResolverContext();

  const { setProvinceId, provinceId } = useProvinceContext();
  const settings = useSettingsContext();
  const { getFeatureFlags } = useFeatureFlags();
  const {
    isSearchTypeaheadEnabled: isShowTypeahead,
    EnableHeliosSnowplow: snowplowEnabled,
    isSearchColdStartEnabled,
  } = getFeatureFlags();
  const env = settings.env;
  const config = useConfig(env);
  const locale = settings.locale as SDMLanguageType;
  const { router } = useSDMRouterContext();
  const { cartCount, entries, id: cart_id } = useSDMCartContext();
  const { firstName, lastName, customerId } = useSDMCustomerContext();
  const { trackEvent } = useAnalyticsContext();
  const redirectToPage = useRedirectLink(router);
  const i18n = useSimpleTranslation();
  const [isOpen, setOpen] = useState(false);
  const [queryList, setQueryList] = useState<IQueryItem[]>([]);
  const [searchBarValue, setSearchBarValue] = useState<string | number>("");
  const [isRedirected, setIsRedirected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const eventDataRef = useRef<{ type: string; context: unknown } | null>(null);
  const [viewDefinition, setViewDefinition] =
    useState<TViewDefinition | null>();
  const configRef = useRef(config);
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  //Autocomplete Component
  const [focus, setFocus] = useState<boolean>(false);
  const childRef = useRef<IAutoCompleteRef>();
  const [yourSearches, setYourSearches] = useLocalStorage<
    ITypeAheadSearchItem[]
  >(YOUR_SEARCH_ITEMS, []);

  const onClose = useCallback(() => {
    setOpen(!isOpen);
  }, [isOpen]);

  const updateQueryParam = useUpdateQueryParam();
  const { sp07, sp05 } = useTheme() as {
    sp07: string;
    sp05: string;
  };

  const handleSearch = (
    val: string | number,
    type: SearchTermsType = SearchTermsType.MANUAL_SEARCH
  ) => {
    const isSelectedFromSuggestions = [
      SearchTermsType.PREVIOUS_SEARCH_TERM,
      SearchTermsType.TRENDING_SEARCH_TERM,
    ].includes(type);
    sessionStorage.setItem(
      SEARCH_TERMS_DATA_KEY,
      JSON.stringify({
        searchTermsType: type,
        typedSearchTerms: isSelectedFromSuggestions
          ? ""
          : searchBarValue.toString(),
      })
    );

    if (
      SEARCH_COPY_MAP[locale] &&
      val.toString() === SEARCH_COPY_MAP[locale].EMPTY_MESSAGE
    ) {
      return;
    }
    const param = {
      text:
        // Remove the section key from the search term
        trimSectionKey(val.toString() || searchBarValue.toString()),
      lang: locale,
    };
    const updatedUrl = updateQueryParam(param);
    //update local storage
    setYourSearches(updateYourSearches(param.text));
    redirectToPage(updatedUrl);
    setFocus(false);
  };

  const handleSelectionChange = useCallback(
    (val: string | number) => {
      let searchTermsType: SearchTermsType = SearchTermsType.TYPEAHEAD;
      if (typeof val === "string") {
        switch (true) {
          case val.includes(SearchSections.YOUR_SUGGESTIONS):
            searchTermsType = SearchTermsType.PREVIOUS_SEARCH_TERM;
            break;
          case val.includes(SearchSections.TRENDING_SUGGESTIONS):
            searchTermsType = SearchTermsType.TRENDING_SEARCH_TERM;
            break;
          default:
            searchTermsType = SearchTermsType.TYPEAHEAD;
            break;
        }
      }
      handleSearch(val, searchTermsType);
    },
    [handleSearch]
  );

  const trackFnWrapper = useCallback((type: string, context: unknown): void => {
    //TODO: refactor analytics handling methods if required
    const analyticContext = context as any;
    trackEvent("heliosNavClick", {
      event: type,
      component: analyticContext?.component,
      heliosSnowplow: snowplowEnabled,
      heliosAnalytics: true,
    });
  }, []);

  const trackNavClick = useCallback((type: string, context: unknown) => {
    eventDataRef.current = { type, context };
  }, []);

  const handleValueChange = (value: string) => {
    setSearchBarValue(value);
    const COLD_START_DISPLAY_CHAR_LIMIT = 3;
    const searchTermValueLength = value.length;
    if (
      isSearchColdStartEnabled &&
      searchTermValueLength >= COLD_START_DISPLAY_CHAR_LIMIT
    ) {
      (childRef.current as any)?.setClose?.();
      setFocus(false);
    } else {
      setFocus(true);
    }
    const currentConfig = configRef.current;
    if (
      isShowTypeahead &&
      searchTermValueLength >= COLD_START_DISPLAY_CHAR_LIMIT
    ) {
      setLoading(true);
      setHasLoadedOnce(false);
      const { domainUserId } = getSnowplowUserIdentifications?.() ?? {};
      const ciamAccessToken = getCiamAccessToken();
      fetchTypeAheadSearch({
        value,
        config: currentConfig,
        locale,
        cartId: cart_id,
        snowplowSpecifications: {
          domainUserId: domainUserId,
        },
        ciamAccessToken,
        customerId,
        setQueryList,
        onComplete: () => {
          setLoading(false);
          setHasLoadedOnce(true);
        },
      });
    }
  };

  const handleInputChange = (value: string) => {
    if (value.length < SEARCH_CHAR_COUNT) {
      setQueryList([]);
      setLoading(false);
      setHasLoadedOnce(false);
    }
    setSearchBarValue(value);
  };
  const handleOnSelection = (val: string | number) => setSearchBarValue(val);

  const handleRedirectToPage = useCallback(
    (text: string) => {
      setIsRedirected(true);
      redirectToPage(text);
    },
    [redirectToPage]
  );

  useEffect(() => {
    if (isRedirected && eventDataRef.current) {
      const analyticData = eventDataRef.current as any;
      trackEvent("heliosNavClick", {
        event: analyticData.type,
        component: analyticData.context.component,
      });
    }
  }, [isRedirected, firstName, lastName]);

  const setNoWrapper = (components: any[]) => {
    components.forEach((component, i) => {
      component.noOuterWrapper = true;
      component.noOuterWrapperCSS = {
        marginBottom: i !== components.length - 1 ? sp05 : "0",
      };
    });
  };

  const fetchColdStartData = async () => {
    setLoading(true);
    const result = await fetchColdStartSearch({
      config,
      locale,
    });
    setLoading(false);
    const components = (
      result.layout?.sections
        ?.mainContentCollection as unknown as MainContentCollection
    )?.components;
    if (components) {
      setNoWrapper(components);
    }

    setViewDefinition(result);
  };

  // fetch cold start data
  useEffect(() => {
    if (config && isSearchColdStartEnabled) {
      void fetchColdStartData();
    }
  }, [config, isSearchColdStartEnabled]);

  const sdmCustomerInitials = useSdmCustomerInitials({ firstName, lastName });
  let megaMenuData: {
      components: any;
      backgroundColour?: string | undefined;
      alignment?: string | undefined;
    },
    mobileMenuData,
    slimMenuData,
    logoSection,
    logoSectionData,
    storeSection,
    storeSectionData,
    searchSectionData,
    cartSectionData,
    featureServicesSection,
    utilsSection,
    utilsSectionWithSignin,
    informationCompData,
    informationTextData,
    topBarNavItemData,
    topBarNavItemVariant,
    topBarNavDropdown,
    navDropdownVariant,
    mainSectionData,
    megaMenuCompData,
    mobileDrawerMenuData,
    topBarCompData;
  const topBarNavItemSection: any = header["topRightSection"]?.components;
  const topBarProvinceSelector = topBarNavItemSection
    ? topBarNavItemSection.find(
        (item: any) => item.componentId === "provinceListComponent"
      ) ?? null
    : null;
  const provinceSelectorData = topBarProvinceSelector?.data;
  const provincesList = topBarProvinceSelector?.data.provinceList;
  /** adds a new key called 'selected' to the object */
  const provinceDropDownOptions = useMemo(() => {
    const selectedProvinceId = provinceId;
    return provincesList
      ? provincesList.map((province: ProvinceListItem) => {
          return {
            ...province,
            selected: province.code === selectedProvinceId,
          };
        })
      : [];
  }, [provincesList, provinceId]);

  const handleProvinceSelection = (provinceId: string) => {
    setProvinceId(provinceId);
    // ensure the push happens when available and doesn't fail otherwise(drx customer-facing app scenario).
    redirectToPage(router.asPath);
  };

  const accountSection = header["accountSection"];
  const accountSectionData = accountSection?.components?.[0]?.data;
  const navigationItemDropdownItems =
    accountSectionData?.dropdownItems as Dropdown[];
  const transformedAccountDropdownItems = useMemo(() => {
    return navigationItemDropdownItems?.map((item: any) => {
      if (item.componentId === "dropdownItem") {
        return {
          variantId: item.variantId !== null ? item.variantId : undefined,
          callToAction: item.callToAction,
          leftIcon: item.leftIconImage || null,
        };
      } else if (item.componentId === "headerGroupComponent") {
        return {
          sectionHeading: {
            text: item.data.headline.text,
          },
        };
      }
      return {};
    });
  }, [navigationItemDropdownItems]);

  const initializeData = () => {
    megaMenuData = header["categoryNavigationSection"];
    mobileMenuData = megaMenuData?.components;
    slimMenuData = header["bottomSection"];
    logoSection = header["logoSection"]?.components;
    logoSectionData = logoSection[0].data;
    storeSection = header["storeSection"]?.components;
    storeSectionData = storeSection[0]?.data;
    searchSectionData = header["searchSection"]?.components[0]?.data;
    cartSectionData = header["cartSection"]?.components[0]?.data;
    if (typeof entries !== "undefined") {
      cartSectionData["count"] = cartCount;
      cartSectionData["showCount"] = !!cartCount && cartCount > 0;
    }
    featureServicesSection = header["featuredServicesSection"]?.components;
    utilsSection = storeSection?.concat(featureServicesSection);
    utilsSectionWithSignin = accountSectionData
      ? utilsSection?.concat(accountSection?.components)
      : utilsSection?.concat(topBarNavItemSection);
    informationCompData = header["topLeftSection"];
    informationTextData = informationCompData?.components?.[0]?.data;
    topBarNavItemData = topBarNavItemSection
      ? topBarNavItemSection[0]?.data
      : null;
    const topBarNavigationItemComponent = topBarNavItemSection
      ? topBarNavItemSection?.find(
          (item: any) => item.componentId === "navigationItemComponent"
        )
      : null;
    topBarNavItemData = topBarNavigationItemComponent
      ? topBarNavigationItemComponent?.data
      : null;
    topBarNavItemVariant = topBarNavigationItemComponent
      ? topBarNavigationItemComponent?.variantId
      : null;
    topBarNavDropdown = topBarNavItemData?.dropdownItems as Dropdown[];
    navDropdownVariant = topBarNavDropdown?.length ? "STANDARD" : "SIGN_IN";
    mainSectionData = {
      logo: logoSectionData,
      store: storeSectionData ?? null,
      search: searchSectionData
        ? {
            placeholderText: searchSectionData?.displayText1,
            ariaLabelClear: i18n("a11yClearSearchLabel"),
            ariaLabelInput: searchSectionData?.displayText1,
            ariaLabelSearchButton: i18n("a11ySubmitSearch"),
          }
        : null,
      cart: cartSectionData ?? null,
      featuredServices: featureServicesSection,
      handleOnSearch: handleSearch,
      handleOnHamburgerClick: () => setOpen(true),
      handleOnBagClick: redirectToPage,
      handleOnStoreAndServicesClick: redirectToPage,
      ariaLabelOpenMenu: i18n("ariaLabelOpenMenu"),
      showTypeahead: true,
      handleValueChange: handleValueChange,
      handleOnSelection: handleOnSelection,
      searchQueryList: queryList,
      navigationItem: accountSectionData
        ? {
            dropdownItems: transformedAccountDropdownItems,
            callToActionHandler: redirectToPage,
            count: cartCount,
            displayNewSignInButton: !navigationItemDropdownItems.length,
            navItemVariant: navigationItemDropdownItems.length
              ? "STANDARD"
              : "SIGN_IN",
            variantId: accountSection?.components?.[0].variantId,
            customerInitials: navigationItemDropdownItems.length
              ? sdmCustomerInitials
              : null,
            avatarVariant: navigationItemDropdownItems.length
              ? "Signed-in"
              : null,
            callToAction: accountSectionData.callToAction,
            showCount: accountSectionData.showCount,
            dropdownOpenIcon: accountSectionData.dropdownOpenIcon,
            dropdownCloseIcon: accountSectionData.dropdownCloseIcon,
            initialIcon: accountSectionData.initialIcon,
          }
        : null,
    };
    megaMenuCompData = {
      megaMenuV2Data: mobileMenuData,
      a11yData: {
        l3ExploreMessage: i18n("a11yMegaMenuExploreMessage"),
        megaMenuAriaLabel: i18n("a11yMegaMenuAriaLabel"),
      },
      closeButtonLabel: i18n("megaMenuCloseLabel"),
      trackingFn: trackFnWrapper,
      callToActionHandler: redirectToPage,
    };

    mobileDrawerMenuData = {
      trackingFn: trackFnWrapper,
      isDrawerOpen: isOpen,
      onClose,
      headerSectionData: logoSection,
      topSectionData: mobileMenuData,
      bottomSectionData: utilsSectionWithSignin,
    };

    topBarCompData = {
      label: informationTextData?.displayText1,
      callToActionLink: informationTextData?.callToAction,
      variant: informationTextData?.variantId,
      navigationItem: topBarNavItemData
        ? {
            ...topBarNavItemData,
            variantId: topBarNavItemVariant ?? "",
            navItemVariant: navDropdownVariant,
            callToActionHandler: redirectToPage,
          }
        : null,
      provinceSelectorItem: topBarProvinceSelector
        ? {
            displayText1: provinceSelectorData.displayText1 ?? "",
            options: provinceDropDownOptions,
            menuName: "provinceSelector",
            labelId: "provinceSelector",
            onSelected: handleProvinceSelection,
            startIcon: {
              altText: provinceSelectorData.leftIconImage?.altText ?? "",
              url: provinceSelectorData.leftIconImage?.url ?? "",
            },
            labelPosition: "vertical",
            variant: "province",
          }
        : null,
      backgroundColour: informationCompData?.backgroundColour,
    };
  };
  try {
    initializeData();
  } catch {
    // Error initializing masthead data - component will render with defaults
  }
  /**
   * to generate l1, l2 links as an array for SEO crawlable links
   * @returns array of crawlable links
   */
  const generateCrawlableData = () => {
    const crawlableLinks: any[] = megaMenuData?.components?.map(
      (item: { data: any }) => {
        const cta: TSDMCta[] = [];
        const itemData = item?.data as any;
        itemData?.callToAction &&
          itemData?.callToAction?.url !== "/" &&
          cta.push(itemData?.callToAction);
        if (itemData?.l2Data && itemData?.l2Data?.length) {
          itemData?.l2Data?.map((l2: any) => {
            l2?.callToAction &&
              l2?.callToAction?.url !== "/" &&
              cta.push(l2?.callToAction);
          });
        }
        return cta;
      }
    );
    const crawlableLinksArr = Array.prototype.concat.apply([], crawlableLinks);
    return crawlableLinksArr;
  };

  const linkData: TSDMCta[] = generateCrawlableData();

  /** Error function for when masthead component fails */
  const { componentError, setComponentError } = settings;

  const onErrorFn = useCallback(() => {
    if (setComponentError) {
      setComponentError({
        mastheadError: "Broken Masthead",
      });
    }
  }, [componentError, setComponentError]);

  const searchedTerm = searchBarValue.toString();

  // Filter the your searches for autocomplete
  const yourFilteredSearches = useMemo<ITypeAheadSearchItem[]>(() => {
    return yourSearches
      .filter(
        ({ suggestion }) =>
          suggestion.toLowerCase().includes(searchedTerm.toLowerCase()) &&
          suggestion !== "" &&
          searchedTerm.length < SEARCH_CHAR_COUNT
      )
      .slice(0, YOUR_SEARCH_ITEMS_DISPLAY_LIMIT)
      .map(({ suggestion }) => ({
        suggestion,
        isLoading: false,
        isEmptyMessage: false,
      }));
  }, [yourSearches, searchedTerm]);

  const trendingSearches = useMemo<ITypeAheadSearchItem[]>(() => {
    const topCategorySearches = TRENDING_SEARCHES;

    if (searchedTerm.length >= SEARCH_CHAR_COUNT) return [];

    const filtered =
      searchedTerm.length === 0
        ? topCategorySearches
        : topCategorySearches.filter((search) => {
            const words = search.suggestion.toLowerCase().split(" ");
            return words.some((word) =>
              word.startsWith(searchedTerm.toLowerCase())
            );
          });

    const results = filtered.length > 0 ? filtered : topCategorySearches;

    return results
      .slice(0, TRENDING_SEARCH_ITEMS_DISPLAY_LIMIT)
      .map(({ suggestion }) => ({
        suggestion,
        isLoading: false,
        isEmptyMessage: false,
      }));
  }, [searchedTerm]);

  const beautySearches = useMemo<ITypeAheadSearchItem[]>(() => {
    if (!isShowTypeahead) {
      return [];
    }

    return buildTypeaheadSuggestions(
      searchedTerm,
      loading,
      hasLoadedOnce,
      queryList,
      SEARCH_CHAR_COUNT,
      locale
    );
  }, [
    queryList,
    isShowTypeahead,
    searchedTerm,
    loading,
    hasLoadedOnce,
    trendingSearches,
  ]);

  const searchSections = useMemo(() => {
    return [
      {
        key: SearchSections.BEAUTY_SEARCHES,
        title: "",
        items: beautySearches,
      },
      {
        key: SearchSections.YOUR_SUGGESTIONS,
        title: SEARCH_COPY_MAP[locale].RECENT,
        items: yourFilteredSearches,
      },
      {
        key: SearchSections.TRENDING_SUGGESTIONS,
        title: SEARCH_COPY_MAP[locale].TRENDING,
        items: trendingSearches,
      },
    ].filter(
      ({ items }) => items.length && !items.some((item) => item.isEmptyMessage)
    );
  }, [beautySearches, yourFilteredSearches, trendingSearches]);

  const showEmptyMessage = useMemo(() => {
    return beautySearches.some((item) => item.isEmptyMessage);
  }, [beautySearches]);

  // Autocomplete: Set focus to the child ref input if focus is true
  useEffect(() => {
    if (focus) {
      childRef.current?.setOpen();
    }
  }, [searchSections, focus]);

  // Callback on blur of the search input
  const onBlur = useCallback(() => {
    // Close the autocomplete overlay
    setFocus(false);
  }, []);

  const onClearInput = () => {
    setSearchBarValue("");
    // trigger focus
    childRef?.current?.setFocus();
  };

  // Callback on focus of the search input
  const onFocus = useCallback(() => {
    // Open the autocomplete overlay
    setFocus(true);
  }, []);

  const updateYourSearches =
    (term: string | number) => (prev: ITypeAheadSearchItem[]) => {
      const newSearches = [
        {
          suggestion: term.toString(),
          id: Date.now(),
          isRemovable: true,
        },
        ...prev.filter(({ suggestion }) => suggestion !== term.toString()),
      ];
      return newSearches.slice(0, YOUR_SEARCH_ITEMS_DISPLAY_LIMIT);
    };

  return (
    <div data-testid="masthead-section">
      <ErrorBoundary
        onError={onErrorFn}
        FallbackComponent={() => <BreadcrumbsSkeleton />}
      >
        {/* changes for SEO crawlable links for HFE-1663 */}
        {linkData?.map((link: TSDMCta, i: number) => {
          return (
            <a
              style={{ visibility: "hidden", display: "none" }}
              href={link?.url}
              aria-hidden="true"
              data-testid={`crawlable-menu-link-${i}`}
              key={`crawlable-menu-link-${i}`}
            >
              {link?.text}
            </a>
          );
        })}

        <Masthead
          mainSection={mainSectionData as any}
          mobileMenuData={mobileDrawerMenuData as any}
          ctaHandler={handleRedirectToPage}
          trackingFn={trackNavClick}
          slimMenuData={slimMenuData as any}
          megaMenuData={megaMenuCompData as any}
          topSection={topBarCompData as any}
        >
          <CustomAutoCompleteSlot>
            <Autocomplete
              buttonClassName="focus-visible:[box-shadow:0_0_0_3px_rgba(66,_153,_225,_0.6)] w-[15px] h-[15px] mr-[8px]"
              buttonContainerClassName="pr-[8px] pt-[8px] mt-[4px] h-[24px]"
              className="mt-0"
              overlayClassName="z-20 bg-white [&_[data-testid='autocomplete-empty-message']]:font-light text-[14px] font-['GT_Eesti_Text']"
              inputClassName="h-[40px] rounded-[20px] pt-[8px] pb-[8px] pl-[16px] pr-[30px] font-['GT_Eesti_Text'] text-[14px] text-[#1A1F3C]"
              allowsCustomValue
              shouldRenderSearchContent={true}
              clearBtnProps={{
                title: SEARCH_COPY_MAP[locale].CLEAR,
                "aria-label": SEARCH_COPY_MAP[locale].CLEAR,
              }}
              optionClassName="font-['GT_Eesti_Text'] text-[14px] mt-[4px]"
              sectionClassName="mt-[4px]"
              items={searchSections}
              label={SEARCH_COPY_MAP[locale].SEARCH}
              onBlur={onBlur}
              onClearInput={onClearInput}
              onFocus={onFocus}
              // @ts-ignore provinceId is string
              onSelectionChange={handleSelectionChange}
              onValueChange={handleValueChange}
              onInputChange={handleInputChange}
              onValueSubmit={handleSearch}
              placeholder={SEARCH_COPY_MAP[locale].SEARCH}
              ref={childRef as React.RefObject<IAutoCompleteRef>}
              submitBtnProps={{
                title: SEARCH_COPY_MAP[locale].SUBMIT,
                "aria-label": SEARCH_COPY_MAP[locale].SUBMIT,
              }}
              emptyMessage={
                showEmptyMessage
                  ? SEARCH_COPY_MAP[locale].EMPTY_MESSAGE
                  : undefined
              }
            >
              {/* if cold start is not enabled or if loading is true and view definition is not set, show the search sections */}
              {(!isSearchColdStartEnabled ||
                searchedTerm.length >= SEARCH_CHAR_COUNT ||
                (!loading && !viewDefinition)) &&
                (searchSections.map(({ key, title, items }) => (
                  <Section key={key} title={title}>
                    {items.map(({ suggestion, isLoading }) => (
                      <Item
                        key={`${key}::${suggestion}`}
                        textValue={suggestion}
                        aria-disabled={isLoading}
                      >
                        {isLoading ? (
                          <Skeleton
                            className="w-full h-8 rounded-none bg-iceberg-lighter-grey"
                            data-testid="autocomplete-loading"
                          />
                        ) : (
                          <>
                            {key === SearchSections.YOUR_SUGGESTIONS ? (
                              <>
                                <span className="font-bold">{suggestion}</span>
                                <Clock8 className="w-[18px] h-[18px] text-iceberg-muted-grey" />
                              </>
                            ) : (
                              <AutoCompleteHighlightItem
                                suggestion={suggestion}
                                queryParam={searchedTerm}
                                className="font-default font-light"
                              />
                            )}
                          </>
                        )}
                      </Item>
                    ))}
                  </Section>
                )) as any)}

              {/* cold start */}
              {isSearchColdStartEnabled && !loading && viewDefinition && (
                <AutocompleteSearchContentSlot>
                  <Box py={sp07} px={sp05}>
                    <SearchColdStartComponent
                      isLoading={loading}
                      data={viewDefinition}
                    />
                  </Box>
                </AutocompleteSearchContentSlot>
              )}
            </Autocomplete>
          </CustomAutoCompleteSlot>
        </Masthead>
      </ErrorBoundary>
    </div>
  );
};

export default MastheadComponent;
